import { CommonModule } from '@angular/common';
import { Component, HostListener, OnChanges, OnDestroy, OnInit, SimpleChanges, inject, input } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router, RouterModule } from '@angular/router';
import { Subject, takeUntil, timer } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { Notification } from '../../../models/notification.models';
import { AuthService } from '../../../services/auth.service';
import { FeatureFlagService } from '../../../services/feature-flag.service';
import { NotificationService } from '../../../services/notification.service';
import { ReleaseNotesService } from '../../../services/release-notes.service';
import { UserStateService } from '../../../services/user-state.service';
import { resolveNotificationContent } from '../../../utils/mention.utils';
import { ReleaseNotesDialogComponent } from '../release-notes-dialog/release-notes-dialog.component';

export enum HeaderMode {
  AUTHENTICATED = 'authenticated',
  LOGIN = 'login',
}

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [CommonModule, RouterModule],
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
})
export class HeaderComponent implements OnInit, OnChanges, OnDestroy {
  private authService = inject(AuthService);
  private router = inject(Router);
  private featureFlagService = inject(FeatureFlagService);
  private userStateService = inject(UserStateService);
  private notificationService = inject(NotificationService);
  private releaseNotesService = inject(ReleaseNotesService);
  private dialog = inject(MatDialog);

  readonly mode = input<HeaderMode>(HeaderMode.AUTHENTICATED);

  // Expose HeaderMode enum for template use
  readonly HeaderMode = HeaderMode;

  currentUser = '';
  userEmail = '';
  appVersion = environment.appVersion;
  isDropdownOpen = false;
  isNotificationPanelOpen = false;
  isJingling = false;
  isResourcesEnabled = false;
  isClientsMattersEnabled = false;
  isRequestsEnabled = false;
  isNotificationsEnabled = false;
  notificationCount = 0;
  notifications: Notification[] = [];
  readonly HEADER_NOTIFICATION_MAX_COUNT = 5;

  // Release notes state - tracks if current version hasn't been viewed yet
  isNewVersion = true;
  private readonly RELEASE_NOTES_VIEWED_KEY = 'release-notes-viewed-version';

  // User permissions
  canViewResources = false;
  canViewClientMatters = false;
  canViewRequests = false;
  canViewUsersManagement = false;
  canViewPracticeAreasManagement = false;
  canViewFeatureFlagManagement = false;
  hasAnyDashboardPermission = false;

  private destroy$ = new Subject<void>();

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    const target = event.target as HTMLElement;
    if (!target.closest('.user-profile')) {
      this.isDropdownOpen = false;
    }
    if (!target.closest('.notification-container')) {
      this.isNotificationPanelOpen = false;
    }
  }

  ngOnInit(): void {
    // Initial setup based on current mode
    this.handleModeChange();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Handle mode changes from parent component
    if (changes['mode']) {
      this.handleModeChange();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private setupPermissionSubscription(): void {
    // Subscribe to user roles changes to reactively update permissions
    this.authService.userRoles$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.checkUserPermissions();
    });

    // Also check immediately in case roles are already loaded
    this.checkUserPermissions();
  }

  private checkUserPermissions(): void {
    this.canViewResources = this.authService.canViewResources();
    this.canViewClientMatters = this.authService.canViewClientMatters();
    this.canViewRequests = this.authService.canViewRequests();
    this.canViewUsersManagement = this.authService.canViewUsersManagement();
    this.canViewPracticeAreasManagement =
      this.authService.canViewPracticeAreasManagement();
    this.canViewFeatureFlagManagement =
      this.authService.canViewFeatureFlagManagement();

    // Check if user has any dashboard permissions
    this.hasAnyDashboardPermission =
      this.canViewResources ||
      this.canViewClientMatters ||
      this.canViewRequests;
  }

  private clearPermissions(): void {
    this.canViewResources = false;
    this.canViewClientMatters = false;
    this.canViewRequests = false;
    this.canViewUsersManagement = false;
    this.canViewPracticeAreasManagement = false;
    this.canViewFeatureFlagManagement = false;
  }

  private initializeReleaseNotesVersion(): void {
    try {
      const viewedVersion = localStorage.getItem(this.RELEASE_NOTES_VIEWED_KEY);
      this.isNewVersion = viewedVersion !== this.appVersion;
    } catch (error) {
      // localStorage may be unavailable (incognito, iframe, etc) - default to showing "What's New"
      this.isNewVersion = true;
    }
  }

  private markReleaseNotesAsViewed(): void {
    try {
      localStorage.setItem(this.RELEASE_NOTES_VIEWED_KEY, this.appVersion);
    } catch (error) {
      // localStorage may be unavailable - silently fail
      console.debug('Unable to save release notes viewed state to localStorage');
    }
    this.isNewVersion = false;
  }

  private handleModeChange(): void {
    // Only load data and subscribe to changes when in authenticated mode
    if (this.mode() === HeaderMode.AUTHENTICATED) {
      this.initializeReleaseNotesVersion();
      this.loadUserData();
      this.loadHeaderFeatureFlags(); // Use bulk loading instead of individual calls
      this.loadNotifications();
      this.setupPermissionSubscription(); // Subscribe to user permissions
      this.monitorAuthenticationState();
    } else {
      // Clear user data for non-authenticated modes
      this.currentUser = '';
      this.userEmail = '';
      this.isResourcesEnabled = false;
      this.isClientsMattersEnabled = false;
      this.clearPermissions();
    }
  }

  private async loadUserData(): Promise<void> {
    try {
      const isLoggedIn = await this.authService.isLoggedInAsync();
      if (isLoggedIn) {
        this.currentUser = this.authService.getUserName() || 'User';
        this.userEmail = this.authService.getUserEmail() || '';

        // Update the shared user state
        this.userStateService.updateUserData(this.currentUser, this.userEmail);
      } else {
        this.currentUser = 'Guest';
        this.userEmail = '';
        this.userStateService.clearUserData();
      }
    } catch (error) {
      console.error('Error loading user data:', error);
      this.currentUser = 'User';
      this.userEmail = '';
      this.userStateService.updateUserData(this.currentUser, this.userEmail);
    }
  }

  private monitorAuthenticationState(): void {
    // Poll for authentication state changes every 2 seconds when in authenticated mode
    // This helps catch when user info becomes available after login
    timer(0, 2000)
      .pipe(takeUntil(this.destroy$))
      .subscribe(async () => {
        if (this.mode() === HeaderMode.AUTHENTICATED) {
          const isLoggedIn = await this.authService.isLoggedInAsync();
          if (isLoggedIn) {
            const currentUserName = this.authService.getUserName() || 'User';
            const currentUserEmail = this.authService.getUserEmail() || '';

            // Only update if the values have changed to avoid unnecessary updates
            if (
              this.currentUser !== currentUserName ||
              this.userEmail !== currentUserEmail
            ) {
              this.currentUser = currentUserName;
              this.userEmail = currentUserEmail;

              // Update the shared user state
              this.userStateService.updateUserData(
                this.currentUser,
                this.userEmail
              );
            }
          }
        }
      });
  }

  toggleDropdown(event: Event): void {
    event.stopPropagation(); // Prevent document click from immediately closing
    this.isDropdownOpen = !this.isDropdownOpen;
    this.isNotificationPanelOpen = false;
  }

  toggleNotificationPanel(event: Event): void {
    event.stopPropagation();
    this.isNotificationPanelOpen = !this.isNotificationPanelOpen;
    this.isDropdownOpen = false;
  }

  private triggerJingle(): void {
    this.isJingling = true;
    setTimeout(() => (this.isJingling = false), 500);

    const audio = new Audio('notification-blip.mp3');
    audio.play().catch(() => {
      // Browser may block autoplay until user interacts with the page
    });
  }

  viewAllNotifications(): void {
    this.isNotificationPanelOpen = false;
    this.router.navigate(['/user-profile'], { fragment: 'notifications' });
  }

  getDisplayMessage(notification: Notification): string {
    return resolveNotificationContent(
      notification.message,
      notification.notificationType,
      notification.mentions,
    );
  }

  onNotificationMessageClick(event: MouseEvent, notification: Notification): void {
    this.notificationService.markAsRead(notification.id).subscribe();

    const target = event.target as HTMLElement;
    if (target.classList.contains('mention-link')) {
      event.preventDefault();
      event.stopPropagation();
      const href = target.getAttribute('href');
      if (href) {
        this.isNotificationPanelOpen = false;
        this.router.navigateByUrl(href);
      }
    }
  }

  navigateToDashboard(): void {
    // Redirect basic users (no dashboard permissions) to their profile page
    if (!this.hasAnyDashboardPermission) {
      this.router.navigate(['/user-profile']);
    } else {
      this.router.navigate(['/dashboard']);
    }
  }

  async navigateToUserProfile(): Promise<void> {
    this.isDropdownOpen = false;
    this.router.navigate(['/user-profile']);
  }

  async signOut(): Promise<void> {
    this.isDropdownOpen = false;
    try {
      // Signs out of this app only: the user's Microsoft session in the
      // browser is left alone. The service clears local state and replaces
      // the page with the login screen.
      await this.authService.logout();
    } catch (error) {
      console.error('Logout error:', error);
    }
  }

  openReleaseNotes(): void {
    this.isDropdownOpen = false;
    // Mark current version as viewed and update label
    this.markReleaseNotesAsViewed();

    this.releaseNotesService.getReleaseNotes().subscribe({
      next: (content) => {
        this.dialog.open(ReleaseNotesDialogComponent, {
          data: { content },
          maxWidth: '700px',
          width: '90vw',
        });
      },
      error: (error) => {
        console.error('Error loading release notes:', error);
      },
    });
  }

  private checkClientsMattersFeatureFlag(): void {
    // Only check feature flags when in authenticated mode
    if (this.mode() === HeaderMode.AUTHENTICATED) {
      this.featureFlagService
        .isFeatureFlagEnabled('clients-matters')
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (isEnabled) => {
            this.isClientsMattersEnabled = isEnabled;
          },
          error: (error) => {
            console.error(
              'Error checking clients-matters feature flag:',
              error
            );
            // Default to disabled if there's an error
            this.isClientsMattersEnabled = false;
          },
        });
    } else {
      this.isClientsMattersEnabled = false;
    }
  }

  private loadNotifications(): void {
    // Subscribe to the live BehaviorSubject streams for real-time updates
    this.notificationService.notifications$
      .pipe(takeUntil(this.destroy$))
      .subscribe((notifications) => {
        this.notifications = notifications;
      });

    this.notificationService.unreadCount$
      .pipe(takeUntil(this.destroy$))
      .subscribe((count) => {
        this.notificationCount = count;
      });

    // Jingle only when a new notification is received
    this.notificationService.newNotification$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.triggerJingle();
      });

    // Initial load from API + start SignalR connection
    this.notificationService.getNotifications()
      .pipe(takeUntil(this.destroy$))
      .subscribe();

    this.notificationService.initializeSignalR().catch((err) => {
      console.error('Failed to connect to notification hub:', err);
    });
  }

  // New method to load all header feature flags in bulk
  private loadHeaderFeatureFlags(): void {
    if (this.mode() === HeaderMode.AUTHENTICATED) {
      const requiredFlags = ['resources', 'clients-matters', 'requests', 'in-app-notifications'];

      this.featureFlagService
        .loadFeatureFlags(requiredFlags)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (flagMap) => {
            this.isResourcesEnabled = flagMap.get('resources') || false;
            this.isClientsMattersEnabled =
              flagMap.get('clients-matters') || false;
            this.isRequestsEnabled = flagMap.get('requests') || false;
            this.isNotificationsEnabled = flagMap.get('in-app-notifications') || false;
          },
          error: (error) => {
            console.error('Error loading header feature flags:', error);
            this.isResourcesEnabled = false;
            this.isClientsMattersEnabled = false;
            this.isRequestsEnabled = false;
            this.isNotificationsEnabled = false;
          },
        });
    } else {
      this.isResourcesEnabled = false;
      this.isClientsMattersEnabled = false;
      this.isRequestsEnabled = false;
      this.isNotificationsEnabled = false;
    }
  }
}
